// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { USER_TYPE } from '@/types/user';
import { parseToken } from './permissions';

import type { Awaitable } from 'vitest';

export const checkUserType = (token) => {
    if (!token) return;

    const userAttr = parseToken(token);

    const companies = userAttr['custom:Companies'];
    const locations = userAttr['custom:Locations'];
    const isSuperAdmin = userAttr['custom:UserEditRole'];

    let userType = USER_TYPE.INVALID;

    if (locations && locations !== '' && locations !== '*') {
        userType = USER_TYPE.LOCATION;
    } else if (companies === '*' && isSuperAdmin === '1') {
        userType = USER_TYPE.SUPER_ADMIN;
    } else if (companies === '*') {
        userType = USER_TYPE.ADMIN;
    } else if (companies && companies !== '') {
        userType = USER_TYPE.COMPANIES;
    } else {
        userType = USER_TYPE.INVALID;
    }

    return userType;
};

export const hasResellerViewPermission = (token) => {
    if (!token) return false;

    const userType = checkUserType(token);

    // return userAttr['custom:Resellers'] && userAttr['custom:Resellers'] !== ''
    return userType !== USER_TYPE.LOCATION;
};

export const isTokenExpired = (token) => {
    if (!token) return true;

    const attributes = parseToken(token);

    if (attributes.exp * 1000 - Date.now() < 10000) {
        return true;
    }

    return false;
};

export const getUsername = (token) => {
    if (!token) return;

    const userAttr = parseToken(token);

    const firstName = userAttr['given_name'];
    const lastName = userAttr['family_name'];
    const email = userAttr['cognito:username'];

    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    } else if (firstName) {
        return firstName;
    } else if (lastName) {
        return lastName;
    }

    return email;
};

export const getNameInitials = (token) => {
    if (!token) return;

    const userAttr = parseToken(token);

    const firstName = userAttr['given_name'];
    const lastName = userAttr['family_name'];
    const email = userAttr['cognito:username'];

    let userInitials;

    if (firstName && lastName) {
        userInitials = `${firstName[0]}${lastName[0]}`;
    } else if (firstName) {
        userInitials = firstName.substring(0, 2);
    } else if (lastName) {
        userInitials = lastName.substring(0, 2);
    } else {
        userInitials = email.substring(0, 2);
    }

    return userInitials.toUpperCase();
};

type WithRetryOptions<Return> = {
    attempts: number;
    func: () => Awaitable<Return>;
};

/**
 * Wrap a function to automatically retry a certain number of `times` before
 * finally throwing its error.
 */
export const withRetry = <Return>({ attempts, func }: WithRetryOptions<Return>) => {
    const flow = async (retries = 0): Promise<Return> => {
        try {
            const result = await func();
            return result;
        } catch (error) {
            if (retries >= attempts) {
                throw error;
            }
            return flow(++retries);
        }
    };

    return flow(0);
};

/**
 * **Only for usage in System Management.**
 *
 * Convert a `companyId` and a `resellerCompanyId` into an object with a
 * `resellerId` and a `companyId`.
 *
 * This is necessary due to the fact that in the database, `CompanyID` can
 * either be that of a parent company, or of a child reseller. If the company
 * is a reseller, then the `CompanyID` is the `resellerId` and `ResellerCompanyID`
 * is the `companyId`. If the company is not a reseller, then the `resellerId`
 * will be `null`, and `CompanyID` will map to `companyId`.
 */
export const getCompanyAndResellerIds = (companyId: number | null, resellerCompanyId: number | null) => {
    // * If companyId and (reseller/parent)CompanyId are equal, then it's not a reseller.
    // ? If the resellerCompanyId is null, then it's definitely not a reseller, as
    // ? that means it has no parent.
    const isReseller = companyId !== resellerCompanyId && resellerCompanyId !== null;

    return {
        resellerId: isReseller ? companyId : null,
        companyId: isReseller ? resellerCompanyId : companyId,
    };
};


export const keymap = (record: IdMap) => {
  if(record){
    return Object.entries(record).map(([id, name]) => {
      return {
        id: id.toString(),
        name
      } as { id: string, name: string};
    });
  }

  return [];
};